import { isTerminalState } from '../common/processingStates';
import { classifyComprobanteSubtype, formatComprobanteLabel } from './fiscalComprobante';

const TERMINAL_QUEUE = new Set([
  'done',
  'error',
  'duplicate',
  'needs_review',
]);

const COMPLETED_STATUSES = new Set(['PROCESS_COMPLETED', 'PROCESS_PARTIALLY_COMPLETED']);

export function isTerminalQueueStatus(status) {
  return TERMINAL_QUEUE.has(status);
}

function inferDocTypeFromBedrock(bedrock) {
  if (!bedrock || typeof bedrock !== 'object') return null;
  const tipo = String(
    bedrock.tipo_documento || bedrock.categoria_documento || bedrock.title || '',
  ).toLowerCase();
  if (!tipo) return null;
  if (/nota de cr[eé]dito|nota de d[eé]bito|credito|crédito|debito|débito/.test(tipo)) {
    return 'invoices';
  }
  if (/factura|invoice|fc|comprobante/.test(tipo)) return 'invoices';
  if (/recibo|receipt|transferencia/.test(tipo)) return 'receipts';
  return null;
}

/** Resolved type for queue display (DPS doc_type or Bedrock hint). */
export function resolveEffectiveDocType(doc) {
  const raw = (doc?.doc_type || '').trim().toLowerCase();
  if (raw && raw !== 'unclassified') return doc.doc_type;
  const inferred = inferDocTypeFromBedrock(doc?.bedrock_model_output);
  if (inferred) return inferred;
  return doc?.doc_type || 'unclassified';
}

export function isUnclassifiedDoc(doc) {
  return resolveEffectiveDocType(doc).toLowerCase() === 'unclassified';
}

function bedrockSignals(bedrock) {
  if (!bedrock || typeof bedrock !== 'object') return null;

  const tipo = (bedrock.tipo_documento || bedrock.categoria_documento || '').trim();
  if (tipo) return { ...bedrock, tipo_documento: tipo };

  const title = String(bedrock.title || '');
  if (/nota de cr[eé]dito/i.test(title)) {
    return { ...bedrock, tipo_documento: 'Nota de credito' };
  }
  if (/nota de d[eé]bito/i.test(title)) {
    return { ...bedrock, tipo_documento: 'Nota de debito' };
  }
  if (/^factura\b/i.test(title)) {
    return { ...bedrock, tipo_documento: 'Factura' };
  }

  if (bedrock.codigo_factura || bedrock['codigo factura']) return bedrock;
  return null;
}

function comprobanteMeta(bedrock) {
  const signals = bedrockSignals(bedrock);
  if (!signals) {
    return { comprobante_label: null, comprobante_subtype: null };
  }
  return {
    comprobante_label: formatComprobanteLabel(signals),
    comprobante_subtype: classifyComprobanteSubtype(signals),
  };
}

function withComprobanteMeta(doc, payload) {
  return { ...payload, ...comprobanteMeta(doc?.bedrock_model_output), arca_validation: doc?.arca_validation || null };
}

/** Map DocumentProcessingStatus row → SPEC-13 queue status (legacy GraphQL poll). */
export function mapDocumentToUploadStatus(doc) {
  if (!doc) {
    return {
      status: 'queued',
      doc_id: null,
      doc_type: null,
      duplicate_of: null,
      error: null,
      comprobante_label: null,
      comprobante_subtype: null,
    };
  }

  const rawType = (doc.doc_type || '').trim().toLowerCase();
  const redirectId = (doc.redirect_target_document_id || '').trim() || null;
  let doc_id = doc.document_id;
  let doc_type = resolveEffectiveDocType(doc);
  const dps = (doc.status || '').toUpperCase();

  if (redirectId && redirectId !== doc_id) {
    doc_id = redirectId;
    doc_type = doc.redirect_target_doc_type || doc_type;
  }

  if (dps === 'REPROCESS_QUEUED') {
    return withComprobanteMeta(doc, {
      status: 'reprocessing',
      doc_id,
      doc_type,
      duplicate_of: null,
      error: null,
    });
  }

  if (dps === 'DUPLICATE_SKIPPED') {
    return withComprobanteMeta(doc, {
      status: 'duplicate',
      doc_id,
      doc_type,
      duplicate_of: doc.duplicate_of_document_id || null,
      error: null,
    });
  }

  if (dps === 'PROCESS_FAILED' || dps === 'UNSUPPORTED_MEDIA_TYPE') {
    return withComprobanteMeta(doc, {
      status: 'error',
      doc_id,
      doc_type,
      error: doc.error || dps,
      duplicate_of: null,
    });
  }

  if (COMPLETED_STATUSES.has(dps)) {
    if (rawType === 'unclassified') {
      const inferred = inferDocTypeFromBedrock(doc.bedrock_model_output);
      if (redirectId) {
        return withComprobanteMeta(doc, {
          status: 'processing',
          doc_id: redirectId,
          doc_type: doc.redirect_target_doc_type || inferred || doc_type,
          duplicate_of: null,
          error: null,
        });
      }
      if (inferred && inferred !== 'unclassified') {
        return withComprobanteMeta(doc, {
          status: 'processing',
          doc_id,
          doc_type: rawType,
          duplicate_of: null,
          error: null,
        });
      }
      return withComprobanteMeta(doc, {
        status: 'needs_review',
        doc_id,
        doc_type,
        duplicate_of: null,
        error: null,
      });
    }
    return withComprobanteMeta(doc, {
      status: 'done',
      doc_id,
      doc_type,
      duplicate_of: null,
      error: null,
    });
  }

  if (!isTerminalState(dps)) {
    const queueStatus = dps === 'REPROCESS_QUEUED' ? 'reprocessing' : 'processing';
    return withComprobanteMeta(doc, {
      status: queueStatus,
      doc_id,
      doc_type,
      duplicate_of: null,
      error: null,
    });
  }

  return withComprobanteMeta(doc, {
    status: 'queued',
    doc_id,
    doc_type,
    duplicate_of: null,
    error: null,
  });
}
